/**
 * Studio / Advertising / Ops / Platform-role capability helpers.
 * Capabilities are server-derived via POST studio/actor (TpgOpsAuth).
 */
import api from "../../Environment";
import { runtimeConfig } from "../../config/runtimeConfig";

const CACHE_MS = 60 * 1000;
let cache = null;
let cacheAt = 0;
let inflight = null;

function emptyCapabilities() {
  return {
    is_platform_admin: false,
    can_schedule: false,
    can_publish: false,
    can_broadcast: false,
    organization_ids: [],
    creator_ids: [],
    project_ids: [],
    platform_roles: [],
    portals: [],
    role: null,
    can_access_studio: false,
    can_access_advertising: false,
    can_access_ops: false,
    error: true,
  };
}

function normalize(data) {
  const d = data && typeof data === "object" ? data : {};
  const isAdmin = !!d.is_platform_admin;
  const orgIds = Array.isArray(d.organization_ids) ? d.organization_ids : [];
  const creatorIds = Array.isArray(d.creator_ids) ? d.creator_ids : [];
  const projectIds = Array.isArray(d.project_ids) ? d.project_ids : [];
  const platformRoles = Array.isArray(d.platform_roles) ? d.platform_roles : [];
  const portals = Array.isArray(d.portals) ? d.portals : [];
  const canSchedule = !!d.can_schedule;
  const canPublish = !!d.can_publish;
  const canBroadcast = !!d.can_broadcast;

  const canAccessStudio =
    d.can_access_studio != null
      ? !!d.can_access_studio
      : isAdmin ||
        orgIds.length > 0 ||
        creatorIds.length > 0 ||
        projectIds.length > 0 ||
        canSchedule;

  const canAccessAdvertising =
    d.can_access_advertising != null
      ? !!d.can_access_advertising
      : isAdmin;

  const canAccessOps =
    d.can_access_ops != null
      ? !!d.can_access_ops
      : isAdmin || canBroadcast || canSchedule || orgIds.length > 0;

  return {
    is_platform_admin: isAdmin,
    can_schedule: canSchedule,
    can_publish: canPublish,
    can_broadcast: canBroadcast,
    organization_ids: orgIds,
    creator_ids: creatorIds,
    project_ids: projectIds,
    platform_roles: platformRoles,
    portals: portals,
    memberships: Array.isArray(d.memberships) ? d.memberships : [],
    role: d.role || null,
    can_access_studio: canAccessStudio,
    can_access_advertising: canAccessAdvertising,
    can_access_ops: canAccessOps,
    error: false,
  };
}

export function clearStudioCapabilitiesCache() {
  cache = null;
  cacheAt = 0;
  inflight = null;
}

/**
 * @param {{ force?: boolean }} [opts]
 * @returns {Promise<object>}
 */
export async function fetchStudioCapabilities(opts = {}) {
  const force = !!opts.force;
  const now = Date.now();
  if (!force && cache && now - cacheAt < CACHE_MS) {
    return cache;
  }
  if (!force && inflight) {
    return inflight;
  }

  const hasSession =
    localStorage.getItem("userId") && localStorage.getItem("accessToken");
  if (!hasSession) {
    cache = emptyCapabilities();
    cacheAt = now;
    return cache;
  }

  inflight = (async () => {
    try {
      const response = await api.postMethod("studio/actor", {});
      const body = response && response.data ? response.data : response;
      if (body && body.success && body.data) {
        cache = normalize(body.data);
      } else {
        cache = emptyCapabilities();
      }
    } catch (e) {
      cache = emptyCapabilities();
    }
    cacheAt = Date.now();
    inflight = null;
    return cache;
  })();

  return inflight;
}

/**
 * @param {object} caps
 * @param {"studio"|"advertising"|"ops"|"admin"} requirement
 */
export function hasStudioRequirement(caps, requirement) {
  if (!caps || caps.error) {
    return false;
  }
  // Platform admins always satisfy studio/ops/advertising/admin gates.
  // (Scheduler admin UI moved to Blade /admin/scheduler — see TPG_SCHEDULER_ADMIN_OWNERSHIP.md)
  if (caps.is_platform_admin) {
    return true;
  }
  switch (requirement) {
    case "advertising":
      return !!caps.can_access_advertising;
    case "admin":
      return !!caps.can_access_advertising || !!caps.is_platform_admin;
    case "ops":
      return !!caps.can_access_ops;
    case "studio":
    default:
      return !!caps.can_access_studio;
  }
}

/**
 * Resolve Laravel origin (no trailing slash) from adminBaseUrl.
 */
export function resolveLaravelOrigin() {
  const admin = String(runtimeConfig.adminBaseUrl || "").replace(/\/+$/, "");
  if (/\/admin$/i.test(admin)) {
    return admin.replace(/\/admin$/i, "");
  }
  return admin;
}

/**
 * Issue handoff and navigate to Blade portal (sales-rep / admin / moderator).
 * @param {object} portal  entry from caps.portals
 */
export async function openPlatformPortal(portal) {
  if (!portal) {
    return;
  }

  if (portal.kind === "spa") {
    const entities = Array.isArray(portal.entities) ? portal.entities : [];
    if (entities.length > 1) {
      const choice = window.prompt(
        `Select entity id for ${portal.label}:\n` +
          entities.map((e) => e.id).join(", "),
        String(entities[0].id)
      );
      if (!choice) {
        return;
      }
      const match = entities.find((e) => String(e.id) === String(choice));
      if (match && match.path) {
        window.location.assign(match.path);
        return;
      }
    }
    const path = portal.path || "/studio";
    if (path.startsWith("http")) {
      window.location.assign(path);
    } else {
      // SPA in-app navigation preferred when same origin; hard nav still works under CRA.
      window.location.assign(path);
    }
    return;
  }

  if (!portal.requires_handoff) {
    const origin = resolveLaravelOrigin();
    window.location.assign(origin + (portal.path || "/"));
    return;
  }

  try {
    const response = await api.postMethod("platform-roles/handoff", {
      portal: portal.portal,
      role: portal.role,
    });
    const body = response && response.data ? response.data : response;
    if (body && body.success && body.data && body.data.url) {
      window.location.assign(body.data.url);
      return;
    }
    window.alert(
      (body && body.error_messages) ||
        "Unable to open portal. You may not have an active role."
    );
  } catch (e) {
    window.alert("Unable to open portal. Please try again.");
  }
}
